import { getProjectsQueryKey, postProjects } from "@imi/api-client/index.js";
import { useToast } from "@src/shared/components/Toast/useToast";
import { useAppForm } from "@src/shared/helpers/form";
import { logger } from "@src/shared/helpers/logger";
import { parseApiError } from "@src/shared/helpers/parseApiError";
import { revalidateLogic } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { tryit } from "radashi";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod/v4";

const schema = z.object({
  description: z.string().max(250),
  name: z.string().min(1, { message: "Name is required" }),
});

export function useProjectAddForm() {
  const { t } = useTranslation("project", { keyPrefix: "add" });
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useAppForm({
    defaultValues: {
      description: "",
      name: "",
    },
    onSubmit: async ({ formApi, value }) => {
      setSubmitting(true);

      const [error, result] = await tryit(postProjects)({
        body: {
          description: value.description || null,
          name: value.name,
        },
      });
      setSubmitting(false);
      if (error) {
        const apiError = parseApiError({
          error,
          pathAliases: { pipeId: "pipes", pipeSeriesId: "pipeSeries" },
        });
        logger.error("Failed to create project", {
          apiError,
          error,
        });
        formApi.setErrorMap({
          onSubmit: {
            fields: apiError.fields
              ? Object.fromEntries(
                  Object.entries(apiError.fields).map(([key, messages]) => [
                    key,
                    Array.isArray(messages) ? messages.join(", ") : messages,
                  ]),
                )
              : {},
            form: apiError.form ?? undefined,
          },
        });
        return;
      }

      queryClient.invalidateQueries({ queryKey: getProjectsQueryKey() });
      addToast({
        duration: 3000,
        message: t("success.description"),
        title: t("success.title"),
      });
      navigate(`/projects/${result.data.id}`);
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: schema },
  });

  return { form, submitting };
}
